import { useState } from "react";
import { CloudRain, Activity, Tornado, Newspaper, Waypoints, Layers, MapPin, Flame, Satellite, Moon, X, Eye } from "lucide-react";
import { MMI_SCALE } from "@/components/map/ShakeMapLayer";
import { BASEMAPS } from "@/lib/map";

const MODES = [
  ["rate", "Rate"],
  [1, "24h"],
  [3, "3-day"],
  [7, "7-day"],
];
const SEG = "flex-1 flex items-center justify-center gap-1.5 h-7 text-[10px] font-mono uppercase tracking-wider transition-colors";
const segCls = (on) => `${SEG} ${on ? "bg-atlas-cyan text-atlas-void" : "text-atlas-secondary hover:text-white"}`;

const Group = ({ title, children }) => (
  <div className="flex flex-col gap-1.5 px-3 py-2.5 border-t border-white/10 first:border-t-0">
    <div className="text-[9px] font-mono uppercase tracking-[0.2em] text-atlas-cyan/60">{title}</div>
    {children}
  </div>
);

const Segmented = ({ items, value, onChange, testid }) => (
  <div className="flex border border-white/10 divide-x divide-white/10" data-testid={testid}>
    {items.map(([id, Icon, label, tid]) => (
      <button key={id} onClick={() => onChange(id)} data-testid={tid} className={segCls(value === id)}>
        {Icon && <Icon className="w-3.5 h-3.5" />}
        {label}
      </button>
    ))}
  </div>
);

const Row = ({ on, onClick, icon: Icon, label, count, testid, children }) => (
  <div>
    <button
      onClick={onClick}
      data-testid={testid}
      aria-pressed={on}
      className={`w-full flex items-center gap-2 px-2 h-8 text-[10px] font-mono uppercase tracking-wider border transition-colors ${
        on ? "border-atlas-cyan/60 bg-atlas-cyan/10 text-white" : "border-white/10 text-atlas-secondary hover:text-white hover:border-white/25"
      }`}
    >
      <Icon className={`w-3.5 h-3.5 shrink-0 ${on ? "text-atlas-cyan" : ""}`} />
      <span className="flex-1 text-left truncate">{label}</span>
      {count != null && <span className="opacity-70">{count}</span>}
      <span className={`w-1.5 h-1.5 rounded-full ${on ? "bg-atlas-cyan" : "bg-white/15"}`} />
    </button>
    {on && children && <div className="px-2 pt-1.5 pb-1 flex flex-col gap-1.5">{children}</div>}
  </div>
);

const ShakeLegend = () => (
  <div className="flex items-center gap-1 flex-wrap" data-testid="shakemap-legend">
    <span className="text-[9px] font-mono text-atlas-secondary mr-1">MMI</span>
    {MMI_SCALE.map(([v, c, label]) => (
      <span key={v} title={label} className="flex items-center gap-0.5 text-[9px] font-mono text-atlas-secondary">
        <span className="w-3 h-1" style={{ backgroundColor: c }} />
        {v}
      </span>
    ))}
  </div>
);

const RainControls = ({ meta, mode, setMode, opacity, setOpacity }) => (
  <>
    <div className="flex border border-white/10" data-testid="rainfall-mode">
      {MODES.map(([m, label]) => (
        <button key={m} onClick={() => setMode(m)} data-testid={`rainfall-mode-${m}`} className={segCls(mode === m)}>
          {label}
        </button>
      ))}
    </div>
    <label className="flex items-center gap-2 text-[9px] font-mono text-atlas-secondary">
      Opacity
      <input type="range" min="0.2" max="1" step="0.05" value={opacity} onChange={(e) => setOpacity(Number(e.target.value))}
        data-testid="rainfall-opacity" className="flex-1 accent-cyan-400" />
    </label>
    {mode === "rate" ? (
      <img src={meta.rate_legend_url} alt="IMERG rain rate legend (mm/hr)" className="w-full bg-white/90" data-testid="rainfall-legend" />
    ) : (
      <div className="flex items-center gap-1 flex-wrap" data-testid="rainfall-legend">
        <span className="text-[9px] font-mono text-atlas-secondary mr-1">mm</span>
        {meta.accum_stops.map((s) => (
          <span key={s.mm} className="flex items-center gap-0.5 text-[9px] font-mono text-atlas-secondary">
            <span className="w-3 h-2" style={{ backgroundColor: s.color }} />
            {s.mm}
          </span>
        ))}
      </div>
    )}
    <div className="text-[9px] font-mono text-atlas-secondary/80 leading-snug" data-testid="rainfall-date">
      NASA GPM IMERG · through {meta.latest_date}
      {mode !== "rate" && ` · ${mode === 1 ? "24 h" : `${mode}-day`} accumulation`}
    </div>
  </>
);

const BASE_ICONS = { satellite: Satellite, dark: Moon };

// Collapsed by default: one "Layers" button; opens a fixed-width card with Basemap / View / Overlays.
export const LayerPanel = (p) => {
  const [open, setOpen] = useState(false);
  const active = [p.rep, p.dam, p.cyc, p.shake, p.rain, p.felt].filter(Boolean).length;
  return (
    <div className="flex flex-col items-end gap-1.5" data-testid="layer-panel">
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid="layers-toggle"
        aria-expanded={open}
        className={`flex items-center gap-1.5 px-3 h-8 text-[10px] font-mono uppercase tracking-wider border backdrop-blur transition-colors ${
          open ? "bg-atlas-cyan text-atlas-void border-atlas-cyan" : "bg-atlas-void/80 text-atlas-secondary hover:text-white border-white/10"
        }`}
      >
        {open ? <X className="w-3.5 h-3.5" /> : <Layers className="w-3.5 h-3.5" />}
        Layers
        <span className="opacity-70" data-testid="layers-active-count">{active}</span>
      </button>
      {open && (
        <div className="w-[248px] max-h-[calc(100vh-220px)] overflow-y-auto bg-atlas-void/90 backdrop-blur border border-white/10 atlas-rise" data-testid="layers-card">
          <Group title="Basemap">
            <Segmented
              testid="basemap-toggle" value={p.basemap} onChange={p.setBasemap}
              items={Object.entries(BASEMAPS).map(([id, b]) => [id, BASE_ICONS[id], b.label, `basemap-${id}`])}
            />
          </Group>
          <Group title="View">
            <Segmented
              testid="view-toggle" value={p.viewMode} onChange={p.setViewMode}
              items={[["points", MapPin, "Points", "view-points"], ["heatmap", Flame, "Heatmap", "view-heatmap"]]}
            />
          </Group>
          <Group title="Overlays">
            <Row on={p.mon} onClick={() => p.setMon((v) => !v)} icon={Eye} label="Monitoring (dimmed)" count={p.monCount} testid="monitoring-toggle" />
            <Row on={p.rep} onClick={() => p.setRep((v) => !v)} icon={Newspaper} label="Reported" count={p.repCount} testid="reported-toggle" />
            <Row on={p.dam} onClick={() => p.setDam((v) => !v)} icon={Waypoints} label={p.damFlagged ? `River Dams · ${p.damFlagged} blocked` : "River Dams"} count={p.damCount} testid="dam-toggle" />
            <Row on={p.cyc} onClick={() => p.setCyc((v) => !v)} icon={Tornado} label="Cyclones" count={p.cycCount} testid="cyclone-toggle" />
            <Row on={p.felt} onClick={() => p.setFelt((v) => !v)} icon={Activity} label="Felt quakes M3+" count={p.feltCount} testid="felt-quakes-toggle" />
            <Row on={p.shake} onClick={() => p.setShake((v) => !v)} icon={Activity} label="ShakeMaps" count={p.shakeCount} testid="shakemap-toggle">
              <ShakeLegend />
            </Row>
            <Row on={p.rain} onClick={() => p.setRain((v) => !v)} icon={CloudRain} label="Rainfall" testid="rainfall-toggle">
              {p.rainMeta && (
                <RainControls meta={p.rainMeta} mode={p.rainMode} setMode={p.setRainMode} opacity={p.rainOpacity} setOpacity={p.setRainOpacity} />
              )}
            </Row>
          </Group>
        </div>
      )}
    </div>
  );
};
